import { AnalyticsBrowser } from '@segment/analytics-next';
import type { Analytics } from '@segment/analytics-next';
import { initializeSession } from '@/lib/analytics/sessionManager';

const writeKey = import.meta.env['VITE_SEGMENT_WRITE_KEY'];
const hasValidWriteKey = writeKey && typeof writeKey === 'string' && writeKey.trim().length > 0;

const segmentPromise = AnalyticsBrowser.load(
  { writeKey: hasValidWriteKey ? writeKey.trim() : '' },
  { disable: !hasValidWriteKey },
);

let analyticsInstance: Analytics | null = null;

segmentPromise
  .then(([analytics]) => {
    analyticsInstance = analytics;
    if (hasValidWriteKey) {
      analytics.ready(() => initializeSession(analytics as unknown as AnalyticsBrowser));
    }
  })
  .catch((error) => {
    console.warn('[Segment] Failed to load analytics:', error);
    if (import.meta.env.MODE === 'development') {
      console.error('[Segment] Analytics initialization error:', error);
    }
  });

// Minimal wrapper - only ensures callbacks are called when Segment is disabled
// This is critical for questionnaire navigation
export const segment = {
  track: (event: string, properties?: Record<string, unknown>, callback?: () => void) => {
    if (analyticsInstance) {
      analyticsInstance.track(event, properties, callback);
    } else if (callback) {
      callback(); // Critical: ensure navigation callbacks always fire
    }
  },
  page: (...args: Parameters<Analytics['page']>) => analyticsInstance?.page(...args),
  identify: (...args: Parameters<Analytics['identify']>) => analyticsInstance?.identify(...args),
  screen: (...args: Parameters<Analytics['screen']>) => analyticsInstance?.screen(...args),
  group: (...args: Parameters<Analytics['group']>) => analyticsInstance?.group(...args),
  alias: (...args: Parameters<Analytics['alias']>) => analyticsInstance?.alias(...args),
  reset: () => analyticsInstance?.reset(),
  ready: (callback?: () => void) => {
    if (analyticsInstance && callback) {
      analyticsInstance.ready(callback);
    } else if (callback) {
      callback();
    }
  },
};
